import { useRef, useState } from 'react';
import { RICH_TEXT_MARKERS, highlightMarker, HIGHLIGHT_SWATCHES } from '../utils/richText';
import '../css/rich-text-editor.css';

// A <textarea> that shows a small floating toolbar next to the mouse the
// instant the user selects text inside it (mouseup with a non-collapsed
// selection) - clicking a button wraps the current selection with the
// matching markdown-style marker pair (same slice/concat/setSelectionRange
// restore trick as the original single-button `applyBold` in
// exercise-templates/block-editors/TextBlockEditor.jsx, generalized to all
// 5 decorations). Shared by Topic's and Exercise-template's text editors -
// both were near-duplicate textarea+toolbar implementations before this.
export default function RichTextEditor({ value, onChange, rows, placeholder, autoFocus }) {
   const textareaRef = useRef(null);
   const [toolbar, setToolbar] = useState(null); // {x, y, start, end} while visible

   const handleMouseUp = (e) => {
      const el = textareaRef.current;
      if (!el || el.selectionStart === el.selectionEnd) { setToolbar(null); return; }
      setToolbar({ x: e.clientX, y: e.clientY, start: el.selectionStart, end: el.selectionEnd });
   };

   const wrap = (open, close) => {
      const el = textareaRef.current;
      if (!el || !toolbar) return;
      const { start, end } = toolbar;
      const next = value.slice(0, start) + open + value.slice(start, end) + close + value.slice(end);
      onChange(next);
      setToolbar(null);
      requestAnimationFrame(() => {
         el.focus();
         el.setSelectionRange(start, end + open.length + close.length);
      });
   };

   // Line-prefix, not a symmetric wrap like the other 4 buttons - each line
   // of the selection becomes its own list item (renderRichText.jsx's block
   // pass groups consecutive "- " lines into one <ul>).
   const applyList = () => {
      const el = textareaRef.current;
      if (!el || !toolbar) return;
      const { start, end } = toolbar;
      const selected = value.slice(start, end);
      const listified = selected.split('\n').map((line) => (line.startsWith('- ') ? line : '- ' + line)).join('\n');
      const next = value.slice(0, start) + listified + value.slice(end);
      onChange(next);
      setToolbar(null);
      requestAnimationFrame(() => {
         el.focus();
         el.setSelectionRange(start, start + listified.length);
      });
   };

   return (
      <div className='rich-text-editor'>
         <textarea ref={textareaRef} className='exercise-block-generic-text' rows={rows || 4}
            value={value} placeholder={placeholder} autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            onMouseUp={handleMouseUp}
            onBlur={() => setToolbar(null)} />
         {toolbar ? (
            <div className='rich-text-toolbar' style={{ left: toolbar.x, top: toolbar.y }}
               // mousedown (not click) so this fires before the textarea's own
               // onBlur clears `toolbar` and wipes the selection it needs.
               onMouseDown={(e) => e.preventDefault()}>
               <button type='button' className='rich-text-toolbar-btn' title='Жирний' onClick={() => wrap(...RICH_TEXT_MARKERS.bold)}><b>B</b></button>
               <button type='button' className='rich-text-toolbar-btn' title='Курсив' onClick={() => wrap(...RICH_TEXT_MARKERS.italic)}><i>I</i></button>
               <button type='button' className='rich-text-toolbar-btn' title='Підкреслити' onClick={() => wrap(...RICH_TEXT_MARKERS.underline)}><u>U</u></button>
               <button type='button' className='rich-text-toolbar-btn' title='Закреслити' onClick={() => wrap(...RICH_TEXT_MARKERS.strikethrough)}><s>S</s></button>
               <button type='button' className='rich-text-toolbar-btn' title='Список' onClick={applyList}>☰</button>
               <span className='rich-text-toolbar-sep' />
               {HIGHLIGHT_SWATCHES.map((hex) => (
                  <span key={hex} className='rich-text-toolbar-swatch' style={{ background: '#' + hex }}
                     title='Маркер' onClick={() => wrap(...highlightMarker(hex))} />
               ))}
            </div>
         ) : ''}
      </div>
   );
}
